import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Modal } from '../../../components/common/Modal';
import apiClient from '../../../services/api';
import { Channel, PaginatedResponse } from '../../../types';

export function ChannelsPage() {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const pageSize = 20;

  const [formData, setFormData] = useState({
    name: '',
    provider: 'openai',
    api_endpoint: '',
    api_key: '',
    models: '',
    weight: 1,
    priority: 1,
  });

  useEffect(() => {
    fetchChannels();
  }, [page]);

  const fetchChannels = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get<PaginatedResponse<Channel>>(
        `/admin/channels?page=${page}&page_size=${pageSize}`
      );
      setChannels(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch channels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        models: formData.models.split(',').map(m => m.trim()).filter(Boolean),
      };

      if (editingChannel) {
        await apiClient.put(`/admin/channels/${editingChannel.id}`, payload);
      } else {
        await apiClient.post('/admin/channels', payload);
      }

      setShowModal(false);
      setEditingChannel(null);
      resetForm();
      fetchChannels();
    } catch (error) {
      console.error('Failed to save channel:', error);
    }
  };

  const handleEdit = (channel: Channel) => {
    setEditingChannel(channel);
    setFormData({
      name: channel.name,
      provider: channel.provider,
      api_endpoint: channel.api_endpoint || '',
      api_key: '',
      models: channel.models.join(', '),
      weight: channel.weight,
      priority: channel.priority,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定要删除这个渠道吗？')) return;
    try {
      await apiClient.delete(`/admin/channels/${id}`);
      fetchChannels();
    } catch (error) {
      console.error('Failed to delete channel:', error);
    }
  };

  const handleTest = async (id: number) => {
    try {
      await apiClient.post(`/admin/channels/${id}/test`);
      alert('连接测试成功！');
    } catch (error) {
      alert('连接测试失败');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      provider: 'openai',
      api_endpoint: '',
      api_key: '',
      models: '',
      weight: 1,
      priority: 1,
    });
  };

  const getHealthColor = (status: string) => {
    const colors: Record<string, string> = {
      healthy: 'text-green-600 bg-green-100',
      degraded: 'text-yellow-600 bg-yellow-100',
      unhealthy: 'text-red-600 bg-red-100',
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'text-green-600' : 'text-gray-400';
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">渠道管理</h1>
        <Button
          onClick={() => {
            resetForm();
            setEditingChannel(null);
            setShowModal(true);
          }}
        >
          添加渠道
        </Button>
      </div>

      <Card>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : channels.length === 0 ? (
          <p className="text-center text-gray-500 py-8">暂无渠道</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">名称</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">提供商</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">模型</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">权重</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">优先级</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">健康状态</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">成功率</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">状态</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {channels.map((channel) => (
                    <tr key={channel.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm font-medium">{channel.name}</td>
                      <td className="py-3 px-4 text-sm">{channel.provider}</td>
                      <td className="py-3 px-4 text-sm">
                        <span className="text-xs text-gray-500">
                          {channel.models.slice(0, 2).join(', ')}
                          {channel.models.length > 2 && ` +${channel.models.length - 2}`}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-center">{channel.weight}</td>
                      <td className="py-3 px-4 text-sm text-center">{channel.priority}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getHealthColor(channel.health_status)}`}>
                          {channel.health_status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-center">{channel.success_rate.toFixed(1)}%</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-sm font-medium ${getStatusColor(channel.status)}`}>
                          {channel.status === 'active' ? '启用' : '禁用'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleTest(channel.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            测试
                          </button>
                          <button
                            onClick={() => handleEdit(channel)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            编辑
                          </button>
                          <button
                            onClick={() => handleDelete(channel.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">共 {total} 条记录</div>
                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    上一页
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingChannel(null);
          resetForm();
        }}
        title={editingChannel ? '编辑渠道' : '添加渠道'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">名称</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">提供商</label>
            <select
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="openai">OpenAI</option>
              <option value="claude">Claude</option>
              <option value="wenxin">文心一言</option>
              <option value="tongyi">通义千问</option>
              <option value="custom">自定义</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API Endpoint</label>
            <input
              type="url"
              value={formData.api_endpoint}
              onChange={(e) => setFormData({ ...formData, api_endpoint: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="https://api.openai.com/v1"
              required={!editingChannel}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key {editingChannel && '(留空保持不变)'}
            </label>
            <input
              type="password"
              value={formData.api_key}
              onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              required={!editingChannel}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">模型 (逗号分隔)</label>
            <textarea
              value={formData.models}
              onChange={(e) => setFormData({ ...formData, models: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              rows={3}
              placeholder="gpt-4, gpt-3.5-turbo, gpt-4-turbo"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">权重</label>
              <input
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg"
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">优先级</label>
              <input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border rounded-lg"
                min="1"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setEditingChannel(null);
                resetForm();
              }}
            >
              取消
            </Button>
            <Button type="submit">{editingChannel ? '更新' : '创建'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
